import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../lib/config';
import {
  ChevronLeftIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const FAQ = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [openItems, setOpenItems] = useState(new Set());
  const [cms, setCms] = useState({ title: '', content: '' });
  const [loading, setLoading] = useState(true);
  const ENABLE_CMS = process.env.REACT_APP_ENABLE_CMS === '1';

  useEffect(() => {
    if (!ENABLE_CMS) { setLoading(false); return; }
    let aborted = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_BASE}/content/faq`);
        if (!r.ok) throw new Error('failed');
        const data = await r.json();
        if (!aborted) setCms({ title: data.title || '', content: data.content || '' });
      } catch (_) {
        if (!aborted) setCms({ title: '', content: '' });
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    load();
    return () => { aborted = true; };
  }, []);

  const categories = [
    { id: 'all', name: 'すべて' },
    { id: 'account', name: 'アカウント・登録' },
    { id: 'work', name: 'お仕事について' },
    { id: 'payment', name: '報酬・支払い' },
    { id: 'app', name: 'アプリの使い方' },
    { id: 'trouble', name: 'トラブル・困ったとき' }
  ];

  const faqData = [
    {
      id: 1,
      category: 'account',
      question: 'バイターズとはどのようなサービスですか？',
      answer: 'バイターズは、単発から短期の仕事を見つけることができるアルバイト専門のマッチングサービスです。スマートフォンアプリを通じて、あなたの空いた時間に働ける仕事を簡単に見つけることができます。'
    },
    {
      id: 2,
      category: 'account',
      question: '登録に料金はかかりますか？',
      answer: 'バイターズの利用は完全無料です。登録費用、月額費用、成約手数料などは一切かかりません。'
    },
    {
      id: 3,
      category: 'account',
      question: '本人確認書類は何が使えますか？',
      answer: '運転免許証、パスポート、マイナンバーカード、住民基本台帳カードなどの顔写真付き身分証明書をご利用いただけます。'
    },
    {
      id: 4,
      category: 'work',
      question: 'どのような仕事がありますか？',
      answer: 'カフェスタッフ、コンビニスタッフ、イベントスタッフ、清掃作業、配送・配達など、様々な業種の単発・短期アルバイトをご紹介しています。'
    },
    {
      id: 5,
      category: 'work',
      question: '当日キャンセルはできますか？',
      answer: '体調不良などのやむを得ない理由以外での当日キャンセルは、事業者様にご迷惑をおかけするため控えてください。キャンセルペナルティが適用される場合があります。'
    },
    {
      id: 6,
      category: 'payment',
      question: '報酬の支払いはいつ行われますか？',
      answer: '勤務完了後、事業者による勤務確認が取れ次第、アプリ内ウォレットに報酬が反映されます。通常、勤務日から2-3営業日以内に反映されます。'
    },
    {
      id: 7,
      category: 'payment',
      question: 'ウォレットからお金を引き出すにはどうすればよいですか？',
      answer: 'マイページのウォレット管理から銀行口座を登録し、引き出し申請を行ってください。引き出し手数料として250円が発生いたします。'
    },
    {
      id: 8,
      category: 'app',
      question: 'アプリが正常に動作しません',
      answer: 'アプリを一度終了し、再起動してください。それでも解決しない場合は、アプリを削除して再インストールを試してください。問題が続く場合はお問い合わせください。'
    },
    {
      id: 9,
      category: 'app',
      question: 'プッシュ通知が届きません',
      answer: 'スマートフォンの設定でバイターズアプリの通知が許可されているかご確認ください。また、アプリ内の通知設定も確認してください。'
    },
    {
      id: 10,
      category: 'trouble',
      question: '事業者とトラブルになった場合はどうすればよいですか？',
      answer: 'アプリ内のお問い合わせフォームからご連絡ください。当社がトラブル解決のサポートを行います。必要に応じて事業者との仲裁も行います。'
    },
    {
      id: 11,
      category: 'trouble',
      question: 'パスワードを忘れてしまいました',
      answer: 'ログイン画面の「パスワードを忘れた方」リンクから、登録済みのメールアドレスを入力してパスワードリセット手続きを行ってください。'
    },
    {
      id: 12,
      category: 'account',
      question: '退会したい場合はどうすればよいですか？',
      answer: 'マイページの「退会手続き」から退会手続きを行えます。ただし、未完了の勤務や未引き出しの報酬がある場合は、それらを完了・引き出し後に退会手続きを行ってください。'
    }
  ];

  const toggleItem = (id) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(id)) {
      newOpenItems.delete(id);
    } else {
      newOpenItems.add(id);
    }
    setOpenItems(newOpenItems);
  };

  const filteredFAQ = faqData.filter((item) => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = searchTerm === '' || 
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">{ENABLE_CMS && cms.title?.trim() ? cms.title : 'よくある質問'}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center text-gray-600">読み込み中...</div>
        ) : (ENABLE_CMS && cms.content) ? (
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: cms.content }} />
          </div>
        ) : (
        <>
        {/* 検索とフィルター */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="space-y-4">
            {/* 検索バー */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="質問を検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            {/* カテゴリフィルター */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ リスト */}
        <div className="space-y-4">
          {filteredFAQ.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <QuestionMarkCircleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">該当する質問が見つかりませんでした</h3>
              <p className="text-gray-600">検索条件を変更してお試しください。</p>
            </div>
          ) : (
            filteredFAQ.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleItem(item.id)}
                  className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-gray-900 font-medium pr-4">{item.question}</h3>
                    {openItems.has(item.id) ? (
                      <ChevronUpIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDownIcon className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </div>
                </button>
                
                {openItems.has(item.id) && (
                  <div className="px-6 pb-4">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-700 leading-relaxed">{item.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* お問い合わせリンク */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6 text-center">
          <QuestionMarkCircleIcon className="w-8 h-8 text-blue-600 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">解決しませんでしたか？</h3>
          <p className="text-gray-600 mb-4">お困りの内容について、お気軽にお問い合わせください。</p>
          <button
            onClick={() => navigate('/mypage/contact')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            お問い合わせする
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default FAQ;